import React, { lazy, Suspense } from 'react';
import { createBrowserRouter, RouterProvider, Navigate, Outlet } from 'react-router-dom';

import { useAppSelector } from '@/hooks/redux';
import { selectIsAuthenticated, selectIsLoading } from '@/store/authSlice';

// ---- Layout ----
const AppShell = lazy(() => import('@/components/layout/AppShell'));

// ---- Auth ----
const LoginPage = lazy(() => import('@/pages/LoginPage'));
const ForgotPasswordPage = lazy(() => import('@/pages/ForgotPasswordPage'));
const ResetPasswordPage = lazy(() => import('@/pages/ResetPasswordPage'));

// ---- Core pages ----
const DashboardPage = lazy(() => import('@/pages/DashboardPage'));
const ContactsPage = lazy(() => import('@/pages/ContactsPage'));
const IntegrationsPage = lazy(() => import('@/pages/IntegrationsPage'));
const ConversationsPage = lazy(() => import('@/pages/ConversationsPage'));
const TemplatesPage = lazy(() => import('@/pages/TemplatesPage'));
const CampaignsPage = lazy(() => import('@/pages/CampaignsPage'));

// ---- Implemented pages ----
const ContactDetailPage = lazy(() => import('@/pages/ContactDetailPage'));
const ContactFormPage = lazy(() => import('@/pages/ContactFormPage'));
const TransactionsPage = lazy(() => import('@/pages/TransactionsPage'));
const TransactionDetailPage = lazy(() => import('@/pages/TransactionDetailPage'));
const ContactImportPage = lazy(() => import('@/pages/ContactImportPage'));
const AskAiPage = lazy(() => import('@/pages/AskAiPage'));
const UsersPage = lazy(() => import('@/pages/UsersPage'));
const TeamsPage = lazy(() => import('@/pages/TeamsPage'));
const AuditPage = lazy(() => import('@/pages/AuditPage'));
const AiUsagePage = lazy(() => import('@/pages/AiUsagePage'));
const RolesPage = lazy(() => import('@/pages/RolesPage'));
const MastersPage = lazy(() => import('@/pages/MastersPage'));

// ---- Remaining stub pages ----
function StubPage({ title }: { title: string }) {
  return (
    <div className="flex items-center justify-center h-[calc(100vh-6rem)]">
      <div className="text-center">
        <h2 className="text-2xl font-medium text-[var(--fg-muted)] mb-2">{title}</h2>
        <p className="text-[var(--fg-muted)]">Coming soon in a future milestone</p>
      </div>
    </div>
  );
}


// ---- Route guards ----
function LoadingScreen() {
  return (
    <div className="min-h-screen flex items-center justify-center bg-[var(--bg)]">
      <div className="flex flex-col items-center gap-4">
        <div className="w-12 h-12 rounded-lg bg-[var(--primary)] flex items-center justify-center text-[var(--primary-fg)] font-medium text-lg">A</div>
        <div className="flex items-center gap-2 text-[var(--fg-muted)] text-sm">
          <span className="w-4 h-4 border-2 border-[var(--border)] border-t-[var(--primary)] rounded-full animate-spin inline-block" />
          Loading Allfin CRM...
        </div>
      </div>
    </div>
  );
}

function RequireAuth() {
  const isAuthenticated = useAppSelector(selectIsAuthenticated);
  const isLoading = useAppSelector(selectIsLoading);
  if (isLoading) return <LoadingScreen />;
  if (!isAuthenticated) return <Navigate to="/login" replace />;
  return <Outlet />;
}

function RequireGuest() {
  const isAuthenticated = useAppSelector(selectIsAuthenticated);
  const isLoading = useAppSelector(selectIsLoading);
  if (isLoading) return <LoadingScreen />;
  if (isAuthenticated) return <Navigate to="/dashboard" replace />;
  return <Outlet />;
}

// ---- Router ----
export const router = createBrowserRouter([
  // Guest-only routes
  {
    element: <RequireGuest />,
    children: [
      { path: '/login', element: <Suspense fallback={<LoadingScreen />}><LoginPage /></Suspense> },
      { path: '/forgot-password', element: <Suspense fallback={<LoadingScreen />}><ForgotPasswordPage /></Suspense> },
      { path: '/reset-password', element: <Suspense fallback={<LoadingScreen />}><ResetPasswordPage /></Suspense> },
    ],
  },
  // Authenticated routes
  {
    element: <RequireAuth />,
    children: [
      {
        element: <Suspense fallback={<LoadingScreen />}><AppShell /></Suspense>,
        children: [
          { index: true, element: <Navigate to="/dashboard" replace /> },
          { path: '/dashboard', element: <Suspense fallback={<LoadingScreen />}><DashboardPage /></Suspense> },
          { path: '/contacts', element: <Suspense fallback={<LoadingScreen />}><ContactsPage /></Suspense> },
          { path: '/contacts/new', element: <Suspense fallback={<LoadingScreen />}><ContactFormPage /></Suspense> },
          { path: '/contacts/import', element: <Suspense fallback={<LoadingScreen />}><ContactImportPage /></Suspense> },
          { path: '/contacts/:id', element: <Suspense fallback={<LoadingScreen />}><ContactDetailPage /></Suspense> },
          { path: '/contacts/:id/edit', element: <Suspense fallback={<LoadingScreen />}><ContactFormPage /></Suspense> },
          { path: '/transactions', element: <Suspense fallback={<LoadingScreen />}><TransactionsPage /></Suspense> },
          { path: '/transactions/:id', element: <Suspense fallback={<LoadingScreen />}><TransactionDetailPage /></Suspense> },
          { path: '/conversations', element: <Suspense fallback={<LoadingScreen />}><ConversationsPage /></Suspense> },
          { path: '/templates', element: <Suspense fallback={<LoadingScreen />}><TemplatesPage /></Suspense> },
          { path: '/campaigns', element: <Suspense fallback={<LoadingScreen />}><CampaignsPage /></Suspense> },
          { path: '/ask-ai', element: <Suspense fallback={<LoadingScreen />}><AskAiPage /></Suspense> },
          { path: '/ask-ai/:sessionId', element: <Suspense fallback={<LoadingScreen />}><AskAiPage /></Suspense> },
          { path: '/users', element: <Suspense fallback={<LoadingScreen />}><UsersPage /></Suspense> },
          { path: '/teams', element: <Suspense fallback={<LoadingScreen />}><TeamsPage /></Suspense> },
          { path: '/roles', element: <Suspense fallback={<LoadingScreen />}><RolesPage /></Suspense> },
          { path: '/masters', element: <Suspense fallback={<LoadingScreen />}><MastersPage /></Suspense> },
          { path: '/integrations', element: <Suspense fallback={<LoadingScreen />}><IntegrationsPage /></Suspense> },
          { path: '/audit', element: <Suspense fallback={<LoadingScreen />}><AuditPage /></Suspense> },
          { path: '/ai-usage', element: <Suspense fallback={<LoadingScreen />}><AiUsagePage /></Suspense> },
        ],
      },
    ],
  },
  // Catch-all
  { path: '*', element: <Navigate to="/dashboard" replace /> },
]);

export default function Router() {
  return <RouterProvider router={router} />;
}
